import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import styled from "@emotion/styled";
import {
  ArrowRight,
  Bug,
  Calculator,
  Check,
  CheckCircle2,
  CircleHelp,
  ClipboardList,
  FilePlus2,
  Gauge,
  Headphones,
  Info,
  LayoutDashboard,
  LifeBuoy,
  LogIn,
  TrendingUp,
  X,
} from "lucide-react";
import { categorie, ticketService, tipo } from "../services/ticketService";
import { showSnackbar } from "../../../../redux/slices/snackbar";

const DrawerHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  padding: theme.spacing(2.5, 3),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const DrawerContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: "auto",
  padding: theme.spacing(3),
}));

const DrawerFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.25, 3, 2.5),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
}));

const visuallyHidden = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const normalizeLabel = (value) =>
  String(value || "")
    .trim()
    .toLowerCase();

const normalizeOption = (option, index) => {
  if (typeof option === "string") {
    return { id: option, label: option, value: option };
  }

  const label = option?.label ?? option?.value ?? option?.id ?? "";
  const value = option?.value ?? option?.id ?? label;

  return {
    ...option,
    id: option?.id ?? value ?? index,
    label,
    value,
  };
};

const getCategoryIcon = (label) => {
  const key = normalizeLabel(label);
  if (key.includes("dashboard")) return LayoutDashboard;
  if (key.includes("login") || key.includes("access")) return LogIn;
  if (key.includes("performance")) return Gauge;
  if (key.includes("preventiv")) return Calculator;
  if (key.includes("richiest")) return ClipboardList;
  return CircleHelp;
};

const getTypeInfo = (label) => {
  const key = normalizeLabel(label);

  if (key.includes("bug") || key.includes("malfunzion")) {
    return { icon: Bug, description: "Malfunzionamento" };
  }
  if (key.includes("support")) {
    return { icon: Headphones, description: "Aiuto operativo" };
  }
  if (key.includes("requisit")) {
    return { icon: FilePlus2, description: "Nuova funzionalità" };
  }
  if (key.includes("evolut")) {
    return { icon: TrendingUp, description: "Miglioramento" };
  }

  return { icon: CircleHelp, description: "Tipologia richiesta" };
};

const ChoiceCard = ({
  name,
  label,
  value,
  selected,
  onChange,
  icon: Icon,
  description,
  compact = false,
}) => (
  <Box
    component="label"
    sx={(theme) => ({
      position: "relative",
      display: "flex",
      flexDirection: compact ? "column" : "row",
      alignItems: compact ? "flex-start" : "center",
      justifyContent: compact ? "center" : "flex-start",
      gap: compact ? 1 : 1.25,
      minWidth: 0,
      minHeight: compact ? 82 : 76,
      p: compact ? 1.5 : 1.75,
      border: "1px solid",
      borderColor: selected ? "primary.main" : "divider",
      borderRadius: 2,
      backgroundColor: selected
        ? theme.palette.action.selected
        : theme.palette.background.paper,
      cursor: "pointer",
      transition: theme.transitions.create(
        ["border-color", "background-color", "box-shadow"],
        { duration: theme.transitions.duration.shortest }
      ),
      "&:hover": {
        borderColor: "primary.main",
        backgroundColor: theme.palette.action.hover,
      },
      "&:has(input:focus-visible)": {
        boxShadow: `0 0 0 3px ${theme.palette.action.focus}`,
      },
    })}
  >
    <Box
      component="input"
      type="radio"
      name={name}
      value={value}
      checked={selected}
      onChange={() => onChange(value)}
      sx={visuallyHidden}
    />

    <Box
      sx={{
        display: "grid",
        placeItems: "center",
        flex: "0 0 auto",
        width: compact ? 30 : 36,
        height: compact ? 30 : 36,
        borderRadius: 1.5,
        color: selected ? "primary.main" : "text.secondary",
        bgcolor: selected ? "action.selected" : "action.hover",
      }}
    >
      <Icon size={compact ? 18 : 20} aria-hidden="true" />
    </Box>

    <Box sx={{ minWidth: 0, pr: compact ? 2 : 2.5 }}>
      <Typography
        component="span"
        sx={{
          display: "block",
          fontSize: "0.875rem",
          fontWeight: 600,
          color: "text.primary",
          lineHeight: 1.25,
          overflowWrap: "anywhere",
        }}
      >
        {label}
      </Typography>
      {description && (
        <Typography
          component="span"
          sx={{
            display: "block",
            mt: 0.35,
            color: "text.secondary",
            fontSize: "0.75rem",
            lineHeight: 1.3,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>

    {selected && (
      <Box
        sx={{
          position: "absolute",
          top: 9,
          right: 9,
          display: "grid",
          placeItems: "center",
          width: 20,
          height: 20,
          borderRadius: "50%",
          bgcolor: "primary.main",
          color: "primary.contrastText",
        }}
      >
        <Check size={13} strokeWidth={3} aria-hidden="true" />
      </Box>
    )}
  </Box>
);

const SupportDrawer = ({ open, setOpen }) => {
  const { options: filters } = useSelector((state) => state.issueFilters);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const name = sessionStorage.getItem("nome");
  const cognome = sessionStorage.getItem("cognome");
  const email = sessionStorage.getItem("email");
  const customerName = [name, cognome].filter(Boolean).join(" ") || "Utente";

  const [formData, setFormData] = useState({
    oggetto: "",
    descrizione: "",
    customerName,
    customerEmail: email || "",
    categoria: "",
    tipo: "",
  });

  const normalizedFilters = useMemo(() => {
    if (!Array.isArray(filters)) return [];

    return filters.map((filter) => ({
      ...filter,
      dataset:
        filter.type === "list" && filter.dataset?.dataset
          ? filter.dataset.dataset
          : filter.dataset,
    }));
  }, [filters]);

  const categoriaOptions = useMemo(() => {
    const foundCategoria = normalizedFilters.find(
      (filter) => normalizeLabel(filter.label) === "categoria"
    );
    const source = Array.isArray(foundCategoria?.dataset)
      ? foundCategoria.dataset
      : categorie;

    return source.map(normalizeOption);
  }, [normalizedFilters]);

  const tipoOptions = useMemo(() => {
    const foundTipo = normalizedFilters.find(
      (filter) => normalizeLabel(filter.label) === "tipo"
    );
    const source = Array.isArray(foundTipo?.dataset) ? foundTipo.dataset : tipo;

    return source.map(normalizeOption);
  }, [normalizedFilters]);

  const initials = formData.customerName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  const isFormValid = Boolean(
    formData.categoria &&
      formData.tipo &&
      formData.oggetto.trim() &&
      formData.descrizione.trim()
  );

  const handleSnackbar = (message, severity) => {
    dispatch(showSnackbar({ message, severity }));
  };

  const updateField = (field, value) => {
    setFormData((current) => ({ ...current, [field]: value }));
  };

  const resetForm = () => {
    setFormData((current) => ({
      ...current,
      oggetto: "",
      descrizione: "",
      categoria: "",
      tipo: "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isFormValid || loading) return;

    setLoading(true);
    try {
      const responseData = await ticketService.handleAddTicket({
        oggetto: formData.oggetto.trim(),
        descrizione: formData.descrizione.trim(),
        tipo: formData.tipo,
        categoria: formData.categoria,
      });

      if (responseData.status === 1) {
        handleSnackbar(responseData.message, "success");
        resetForm();
        setOpen(false);
      } else {
        handleSnackbar(responseData.message, "error");
      }
    } catch (error) {
      console.error("Error creating ticket:", error);
      handleSnackbar("Errore durante l'invio della richiesta.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => setOpen(false)}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 560 },
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.default",
        },
      }}
    >
      <DrawerHeader>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}
        >
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              width: 40,
              height: 40,
              flex: "0 0 auto",
              borderRadius: 2,
              color: "primary.main",
              bgcolor: "action.selected",
            }}
          >
            <LifeBuoy size={21} aria-hidden="true" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Richiedi supporto
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.25 }}
            >
              Descrivi il problema e invia una richiesta.
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={() => setOpen(false)}
          size="small"
          aria-label="Chiudi pannello"
        >
          <X size={22} />
        </IconButton>
      </DrawerHeader>

      <DrawerContent>
        <Box
          id="support-ticket-form"
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              bgcolor: "background.paper",
            }}
          >
            <Avatar
              sx={{
                width: 46,
                height: 46,
                flex: "0 0 auto",
                bgcolor: "action.selected",
                color: "primary.main",
                fontWeight: 600,
                fontSize: "0.95rem",
              }}
            >
              {initials}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                component="span"
                sx={{ color: "text.secondary", fontSize: "0.72rem" }}
              >
                Richiedente
              </Typography>
              <Typography
                sx={{
                  mt: 0.15,
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  lineHeight: 1.25,
                }}
              >
                {formData.customerName}
              </Typography>
              <Typography
                sx={{
                  mt: 0.3,
                  color: "text.secondary",
                  fontSize: "0.78rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {formData.customerEmail}
              </Typography>
            </Box>
            <CheckCircle2 size={20} color="currentColor" aria-hidden="true" />
          </Box>

          <Box component="fieldset" sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
            <Typography
              component="legend"
              sx={{ mb: 1.25, fontSize: "0.875rem", fontWeight: 600 }}
            >
              Categoria{" "}
              <Box component="span" sx={{ color: "error.main" }}>
                *
              </Box>
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(3, minmax(0, 1fr))",
                },
                gap: 1.25,
              }}
            >
              {categoriaOptions.map((option) => (
                <ChoiceCard
                  key={option.id}
                  name="categoria"
                  label={option.label}
                  value={option.value}
                  selected={formData.categoria === option.value}
                  onChange={(value) => updateField("categoria", value)}
                  icon={getCategoryIcon(option.label)}
                  compact
                />
              ))}
            </Box>
          </Box>

          <Box component="fieldset" sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
            <Typography
              component="legend"
              sx={{ mb: 1.25, fontSize: "0.875rem", fontWeight: 600 }}
            >
              Tipo{" "}
              <Box component="span" sx={{ color: "error.main" }}>
                *
              </Box>
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                },
                gap: 1.25,
              }}
            >
              {tipoOptions.map((option) => {
                const typeInfo = getTypeInfo(option.label);

                return (
                  <ChoiceCard
                    key={option.id}
                    name="tipo"
                    label={option.label}
                    value={option.value}
                    selected={formData.tipo === option.value}
                    onChange={(value) => updateField("tipo", value)}
                    icon={typeInfo.icon}
                    description={typeInfo.description}
                  />
                );
              })}
            </Box>
          </Box>

          <Box>
            <TextField
              label="Oggetto"
              name="oggetto"
              required
              value={formData.oggetto}
              onChange={(event) => updateField("oggetto", event.target.value)}
              inputProps={{ maxLength: 100 }}
              fullWidth
              size="small"
            />
            <Typography
              component="div"
              sx={{
                mt: 0.5,
                color: "text.secondary",
                fontSize: "0.7rem",
                textAlign: "right",
              }}
            >
              {formData.oggetto.length}/100
            </Typography>
          </Box>

          <Box>
            <TextField
              label="Descrizione"
              name="descrizione"
              required
              multiline
              rows={4}
              value={formData.descrizione}
              onChange={(event) =>
                updateField("descrizione", event.target.value)
              }
              inputProps={{ maxLength: 500 }}
              fullWidth
              sx={{ "& .MuiInputBase-inputMultiline": { resize: "none" } }}
            />
            <Typography
              component="div"
              sx={{
                mt: 0.5,
                color: "text.secondary",
                fontSize: "0.7rem",
                textAlign: "right",
              }}
            >
              {formData.descrizione.length}/500
            </Typography>
          </Box>
        </Box>
      </DrawerContent>

      <DrawerFooter>
        <Typography
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: 1.5,
            color: "text.secondary",
            fontSize: "0.72rem",
          }}
        >
          <Info size={14} aria-hidden="true" />
          Tutti i campi contrassegnati con * sono obbligatori.
        </Typography>
        <Button
          type="submit"
          form="support-ticket-form"
          fullWidth
          variant="contained"
          size="large"
          endIcon={
            loading ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <ArrowRight size={19} />
            )
          }
          disabled={!isFormValid || loading}
          sx={{ minHeight: 48, textTransform: "none", fontWeight: 600 }}
        >
          {loading ? "Invio in corso..." : "Invia richiesta"}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};

export default SupportDrawer;
